import { Analytics } from '@/utils/analytics';
import { AuthStatus } from '@/enums';
import { getSmartOrderQueryWhere } from '@/screens/order-inbounds/queries';
import { gql, useMutation } from '@apollo/client';
import {
  MarkSmartOrderAsReadMutation,
  MarkSmartOrderAsReadMutationVariables,
  UnreadSmartOrdersQuery,
  UnreadSmartOrdersQueryVariables,
} from '@/types';
import { partnerSupplier } from '@/utils/environment';
import { useCallback, useMemo } from 'react';
import { useMaybeUserContext } from '@/contexts/user-context';
import { useOrderInbounds } from './use-order-inbounds';
import { useWrappedQuery } from './use-wrapped-query';

const UNREAD_ORDER_INBOUNDS_QUERY = gql`
  query UnreadSmartOrders($where: smart_order_bool_exp!) {
    smart_order_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`;

export const MARK_ORDER_INBOUND_AS_READ = gql`
  mutation MarkSmartOrderAsRead(
    $businessOrganizationUUID: uuid!
    $orderUUIDs: [uuid!]!
  ) {
    update_smart_order(
      where: {
        supplier: {
          business_organization_uuid: { _eq: $businessOrganizationUUID }
        }
        uuid: { _in: $orderUUIDs }
      }
      _set: { last_viewed_at: "NOW()" }
    ) {
      returning {
        status
        updated_at
        uuid
      }
    }
  }
`;

export const useSmartOrderUnreads = () => {
  const { unknownContactTriageEnabled } = useOrderInbounds();
  const smartOrderQueryWhere = useMemo(
    () =>
      getSmartOrderQueryWhere({
        getUnreadOnly: true,
        unknownContactTriageEnabled,
      }),
    [unknownContactTriageEnabled],
  );
  const maybeUserContext = useMaybeUserContext();

  const { data, refetch } = useWrappedQuery<
    UnreadSmartOrdersQuery,
    UnreadSmartOrdersQueryVariables
  >(UNREAD_ORDER_INBOUNDS_QUERY, {
    skip: maybeUserContext?.authStatus !== AuthStatus.Authenticated,
    variables: {
      where: smartOrderQueryWhere,
    },
  });

  const [markOrderInboundAsRead] = useMutation<
    MarkSmartOrderAsReadMutation,
    MarkSmartOrderAsReadMutationVariables
  >(MARK_ORDER_INBOUND_AS_READ);

  const markSmartOrdersAsRead = useCallback(
    async (smartOrderUUIDs: string[]) => {
      try {
        await markOrderInboundAsRead({
          variables: {
            businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
            orderUUIDs: smartOrderUUIDs,
          },
        });
        Analytics.track('mark_order_inbound_as_read.success', {
          smartOrderUUIDs,
        });

        await refetch();
      } catch (error) {
        Analytics.track('mark_order_inbound_as_read.error', {
          error,
          orderUUIDs: smartOrderUUIDs,
        });
      }
    },
    [markOrderInboundAsRead, refetch],
  );

  const hasUnread = useMemo(() => {
    return (
      data?.smart_order_aggregate.aggregate &&
      data?.smart_order_aggregate.aggregate.count > 0
    );
  }, [data?.smart_order_aggregate.aggregate]);

  return {
    hasUnread,
    markSmartOrdersAsRead,
    refetch,
  };
};
